import { useState, useEffect } from 'react';
import { festivos } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Plus, Trash2, Edit2, Calendar as CalendarIcon, Save, X } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import Modal from '../components/Modal';

// Available years for selection
const AVAILABLE_YEARS = [2024, 2025, 2026, 2027];

const Festivos = () => {
    const { isAdmin } = useAuth();
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [listaFestivos, setListaFestivos] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFestivo, setEditingFestivo] = useState(null);
    const [selectedYear, setSelectedYear] = useState(2026);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

    // Form state
    const [formData, setFormData] = useState({
        nombre: '',
        fecha: '',
        ano: 2026,
        turno_afectado: 'ambos'
    });

    useEffect(() => {
        loadFestivos();
    }, [selectedYear]);

    const loadFestivos = async () => {
        try {
            setLoading(true);
            const res = await festivos.getAll(selectedYear);
            setListaFestivos(res.festivos || []);
        } catch (error) {
            toast.error('Error al cargar festivos: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (festivo = null) => {
        if (festivo) {
            setEditingFestivo(festivo);
            setFormData({
                nombre: festivo.nombre,
                fecha: festivo.fecha.split('T')[0], // Ensure YYYY-MM-DD
                ano: festivo.ano,
                turno_afectado: festivo.turno_afectado || 'ambos'
            });
        } else {
            setEditingFestivo(null);
            setFormData({
                nombre: '',
                fecha: '',
                ano: 2026,
                turno_afectado: 'ambos'
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingFestivo) {
                await festivos.update(editingFestivo.id, formData);
                toast.success('Festivo actualizado correctamente');
            } else {
                await festivos.create(formData);
                toast.success('Festivo creado correctamente');
            }
            setIsModalOpen(false);
            loadFestivos();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        const id = deleteConfirm.id;
        if (!id) return;
        try {
            await festivos.delete(id);
            toast.success('Festivo eliminado');
            loadFestivos();
        } catch (error) {
            toast.error('Error al eliminar: ' + error.message);
        }
    };

    if (loading) {
        return <div className="flex justify-center p-8 text-gray-400">Cargando...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold">Gestión de Festivos</h1>
                    <p className="text-gray-400 mt-1">Configura los días festivos para el cálculo de primas</p>
                </div>
                <div className="flex items-center gap-4">
                    {/* Year Selector */}
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="bg-[#1a1a3e] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500"
                    >
                        {AVAILABLE_YEARS.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                    {isAdmin() && (
                        <button
                            onClick={() => handleOpenModal()}
                            className="btn-primary flex items-center gap-2"
                        >
                            <Plus className="w-5 h-5" />
                            Nuevo Festivo
                        </button>
                    )}
                </div>
            </div>

            <div className="card overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-white/10 bg-white/5">
                            <th className="p-4 font-semibold text-gray-300">Fecha</th>
                            <th className="p-4 font-semibold text-gray-300">Nombre</th>
                            <th className="p-4 font-semibold text-gray-300">Turno</th>
                            <th className="p-4 font-semibold text-gray-300">Día Semana</th>
                            {isAdmin() && <th className="p-4 font-semibold text-gray-300 text-right">Acciones</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {listaFestivos.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="p-8 text-center text-gray-500">
                                    No hay festivos registrados
                                </td>
                            </tr>
                        ) : (
                            listaFestivos.map((festivo) => {
                                const date = new Date(festivo.fecha);
                                const weekday = date.toLocaleDateString('es-ES', { weekday: 'long' });
                                const dateStr = date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

                                return (
                                    <tr key={festivo.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                        <td className="p-4 text-cyan-400 font-medium whitespace-nowrap">
                                            {dateStr}
                                        </td>
                                        <td className="p-4 font-semibold">{festivo.nombre}</td>
                                        <td className="p-4">
                                            {(festivo.turno_afectado === 'M' || festivo.turno_afectado === 'T') ? (
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${festivo.turno_afectado === 'M' ? 'bg-amber-500/20 text-amber-400' : 'bg-indigo-500/20 text-indigo-400'}`}>
                                                    Solo {festivo.turno_afectado === 'M' ? 'Mañana' : 'Tarde'}
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400">
                                                    Ambos
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4 text-gray-400 capitalize">{weekday}</td>
                                        {isAdmin() && (
                                            <td className="p-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleOpenModal(festivo)}
                                                        className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                                        title="Editar"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(festivo.id)}
                                                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingFestivo ? 'Editar Festivo' : 'Nuevo Festivo'}
                footer={(
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="festivo-form"
                            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-[#0f0f2d] font-bold rounded-xl hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all"
                        >
                            {editingFestivo ? 'Guardar Cambios' : 'Crear Festivo'}
                        </button>
                    </div>
                )}
            >
                <form id="festivo-form" onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Nombre del Festivo</label>
                        <input
                            type="text"
                            required
                            value={formData.nombre}
                            onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-all"
                            placeholder="Ej. Año Nuevo"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Fecha</label>
                        <input
                            type="date"
                            required
                            value={formData.fecha}
                            onChange={e => setFormData({ ...formData, fecha: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-all [color-scheme:dark]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Turno Afectado</label>
                        <select
                            value={formData.turno_afectado}
                            onChange={e => setFormData({ ...formData, turno_afectado: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-all"
                        >
                            <option value="ambos" className="bg-[#1a1a3e]">Ambos turnos (día completo)</option>
                            <option value="M" className="bg-[#1a1a3e]">Solo turno Mañana</option>
                            <option value="T" className="bg-[#1a1a3e]">Solo turno Tarde</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-2 ml-1">
                            Selecciona "Solo Mañana" para días como Año Nuevo o Navidad donde solo no trabaja el turno de mañana.
                        </p>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Eliminar Festivo"
                message="¿Estás seguro de que deseas eliminar este festivo?"
                confirmText="Eliminar"
                variant="danger"
            />
        </div>
    );
};

export default Festivos;
